import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import { Tabs, TabsList, TabsTrigger } from "@tenant/ui";
import clsx from "clsx";
import styles from "./FixedPricingTab.module.scss";
import { PricingExplanationDialog } from "@/components/shared";
import { GridV2RefreshedPill } from "@/components/shared/GridV2RefreshedPill";
import { AccountSelectors } from "@/modules/account";
import type { PricingMode } from "@/store/flights/shop/store";

interface FixedPricingTabProps extends PlatformComponentProps {
  onValueChange: (value: PricingMode) => void;
  defaultValue?: PricingMode;
  /**
   * The shopper is priced on Grid V2: the Fixed tab carries the "Refreshed"
   * pill and the tabs drop the info buttons (a button nested in the Radix
   * trigger button, the accessibility defect the Grid V2 design removes).
   */
  isGridV2?: boolean;
}

export const FixedPricingTab = ({
  className,
  onValueChange,
  defaultValue = "fixed",
  isGridV2 = false,
}: FixedPricingTabProps) => {
  const { t, formatFiatCurrency, brand } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "FixedPricingTab");
  const { isMobile } = useDeviceType();

  const [isFixedModalOpen, setIsFixedModalOpen] = useState(false);
  const [isFlexModalOpen, setIsFlexModalOpen] = useState(false);

  // The explanation dialogs are not rendered on Grid V2; drop a pending open
  // state so a flip back to V1 does not pop a dialog nobody asked for.
  useEffect(() => {
    if (isGridV2) {
      setIsFixedModalOpen(false);
      setIsFlexModalOpen(false);
    }
  }, [isGridV2]);

  const basePointsToCashMultiplier = useSelector(
    AccountSelectors.getBasePointsToCashMultiplier
  );

  // Calculate cash value per 100 points (e.g., 0.015 multiplier → $1.50)
  const cashPer100Points = useMemo(() => {
    return basePointsToCashMultiplier ? 100 * basePointsToCashMultiplier : 1;
  }, [basePointsToCashMultiplier]);

  // Emerald cardholders redeem flight add-ons (seats, CFAR) at the grid
  // overage/taxes rate; the pricing explanation modals call this out for them.
  const isEmeraldAccount = useSelector(AccountSelectors.getIsEmeraldAccount);
  const gridOverageAndTaxesPointsToCashMultiplier = useSelector(
    AccountSelectors.getGridOverageAndTaxesPointsToCashMultiplier
  );
  const addOnsCashPer100Points = useMemo(
    () =>
      isEmeraldAccount && gridOverageAndTaxesPointsToCashMultiplier
        ? 100 * gridOverageAndTaxesPointsToCashMultiplier
        : undefined,
    [isEmeraldAccount, gridOverageAndTaxesPointsToCashMultiplier]
  );

  return (
    <>
      <div className={clsx(block, className)}>
        <Tabs onValueChange={onValueChange} defaultValue={defaultValue}>
          <TabsList className={cn("list")}>
            <TabsTrigger
              value="fixed"
              className={cn("trigger", { fixed: true, "grid-v2": isGridV2 })}
            >
              <span className={cn("tab-content")}>
                <span className={cn("tab-title", { "grid-v2": isGridV2 })}>
                  {t("core-flights.fixedPricing.fixedTabTitle")}
                  {isGridV2 ? (
                    <GridV2RefreshedPill
                      size={isMobile ? "compact" : "default"}
                    />
                  ) : (
                    <button
                      className={cn("info-icon")}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsFixedModalOpen(true);
                      }}
                      aria-label={t(
                        "core-flights.fixedPricing.learnAboutFixedPricing"
                      )}
                      type="button"
                    >
                      <Icon
                        iconName={IconNameEnum.infoFilled}
                        useCurrentColor
                      />
                    </button>
                  )}
                </span>
                {!isMobile && (
                  <span className={cn("tab-description")}>
                    {t("core-flights.fixedPricing.fixedTabDescription")}
                  </span>
                )}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="flex"
              className={cn("trigger", { flex: true, "grid-v2": isGridV2 })}
            >
              <span className={cn("tab-content")}>
                <span className={cn("tab-title")}>
                  {t("core-flights.fixedPricing.flexTabTitle")}
                  {!isGridV2 && (
                    <button
                      className={cn("info-icon")}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsFlexModalOpen(true);
                      }}
                      aria-label={t(
                        "core-flights.fixedPricing.learnAboutFlexPricing"
                      )}
                      type="button"
                    >
                      <Icon
                        iconName={IconNameEnum.infoFilled}
                        useCurrentColor
                      />
                    </button>
                  )}
                </span>
                {!isMobile && (
                  <span className={cn("tab-description")}>
                    {t("core-flights.fixedPricing.flexTabDescription", {
                      points: 100,
                      price: formatFiatCurrency({
                        value: cashPer100Points,
                        currencyCode: brand.currency.code,
                      }),
                    })}
                  </span>
                )}
              </span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {!isGridV2 && (
        <>
          <PricingExplanationDialog
            mode="fixed"
            open={isFixedModalOpen}
            onClose={() => setIsFixedModalOpen(false)}
            cashPer100Points={cashPer100Points}
            addOnsCashPer100Points={addOnsCashPer100Points}
          />

          <PricingExplanationDialog
            mode="flex"
            open={isFlexModalOpen}
            onClose={() => setIsFlexModalOpen(false)}
            cashPer100Points={cashPer100Points}
            addOnsCashPer100Points={addOnsCashPer100Points}
          />
        </>
      )}
    </>
  );
};
